import CommonBubble from '@home/components/CommonBubble';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useIntl } from 'umi';
import React, { useEffect, useState, useImperativeHandle } from 'react';
import './index.scss';
import { isArray } from 'lodash';
import { writeFile } from '@sg/utils/downLoadFileUtils';
import { dealDownLoadData } from './dealDownLoadData';
import { Popover, DropdownBox, List } from 'tea-component';
import { captureNode } from '@/pages/globalpedia/utils/file';
import { messageTips } from '@gp/utils/common';
import DownloadIcon from '@/pages/strategic/assests/svg/download-black.svg';

export interface CustomDownloadOption {
  label: string;
  onClick: () => void | Promise<void>;
}

const renderOperations = (props: any, ref: any) => {
  const {
    options,
    defaultValue,
    change,
    defaultValueShow,
    downloadParams,
    enableDownloadDropdown = false,
    cardRef, // Ref to the card element, passed from CardCommon
    onDownloadChart, // Optional override
    onDownloadExcel, // Optional override
    customDownloadOptions, // Custom download options list: Array<{ label: string, onClick: () => void }>
    customDownloadLoading,
  } = props;
  const [operation, setOperation] = useState(defaultValue || '');
  const [format, setFormat] = useState<any>(defaultValueShow || false);
  const [downloadMenuVisible, setDownloadMenuVisible] = useState(false);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { formatMessage } = useIntl();
  // 对父组件暴露的方法
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useImperativeHandle(ref, () => ({
    value: operation,
    setValue: (value: string) => {
      setOperation(value);
    },
    format,
    setFormat: (value: boolean) => {
      setFormat(value);
    }
  }));

  useEffect(() => {
    setOperation(defaultValue || '');
  }, [defaultValue]);

  useEffect(() => {
    setFormat(defaultValueShow || false);
  }, [defaultValueShow]);

  const handleDownloadExcel = () => {
    setDownloadMenuVisible(false);
    const data = options.find((item: any) => item.value === operation);
    change({ ...data, format, downloadType: 'excel' }, 'download');
    if (onDownloadExcel) {
      // Use custom handler if provided
      onDownloadExcel();
    } else if (downloadParams?.exportExcel) {
      downloadParams.exportExcel();
    } else if (downloadParams) {
      // Handle internally using downloadParams
      writeFile(dealDownLoadData(downloadParams), downloadParams.name);
    }
  };

  const handleDownloadChart = async () => {
    setDownloadMenuVisible(false);
    const data = options.find((item: any) => item.value === operation);
    change({ ...data, format, downloadType: 'chart' }, 'download');
    if (onDownloadChart) {
      // Use custom handler if provided
      onDownloadChart();
    } else if (cardRef?.current && downloadParams?.name) {
      // Handle internally using cardRef and downloadParams
      try {
        const node = cardRef.current;
        if (!node) {
          return;
        }
        // Get the actual rendered dimensions including padding and borders
        const rect = node.getBoundingClientRect();
        const fullWidth = rect.width;
        const fullHeight = rect.height;

        await captureNode({
          type: 'image',
          node: node,
          fileName: downloadParams.name,
          nodeOptions: {
            width: fullWidth,
            height: fullHeight,
          },
          options: {
            useCORS: true,
            scale: 2,
            backgroundColor: '#ffffff',
            width: fullWidth,
            height: fullHeight,
            windowWidth: fullWidth,
            windowHeight: fullHeight,
            onclone: (document: Document, element: HTMLElement) => {
              if (!element) {
                return;
              }
              // Hide the operations container (the 4 icons at top right)
              const operationsContainer = element.querySelector('.tea-justify-grid__col--right');
              if (operationsContainer) {
                (operationsContainer as HTMLElement).style.display = 'none';
              }
              // Ensure the card element maintains its full dimensions
              const clonedCard = element.querySelector('.dbs-card-common') || element;
              if (clonedCard) {
                (clonedCard as HTMLElement).style.boxSizing = 'border-box';
                (clonedCard as HTMLElement).style.width = `${fullWidth}px`;
                (clonedCard as HTMLElement).style.height = `${fullHeight}px`;
              }
            },
          },
        });
      } catch (error) {
        console.error('Failed to download chart:', error);
        messageTips('error', 'Failed to download chart');
      }
    }
  };

  return <>
    {options?.map((item: any, index: number) => {
      let tips = null;
      if (item.tip)
        // no need to translate here since the translation is handled in pickOperations already
        // tips = typeof item.tip === 'string' ? formatMessage({ id: item.tip }): item.tip;
        tips = item.tip;

      // Special handling for download icon when dropdown is enabled
      if (item.value === 'download' && enableDownloadDropdown) {
        // Build download menu items
        const renderDownloadMenuItems = () => {
          // If customDownloadOptions is provided, use it
          if (customDownloadOptions && Array.isArray(customDownloadOptions) && customDownloadOptions.length > 0) {
            return customDownloadOptions.map((option: CustomDownloadOption, idx: number) => (
              <List.Item
                key={idx}
                onClick={() => {
                  setDownloadMenuVisible(false);
                  option.onClick();
                }}
                disabled={customDownloadLoading}
              >
                <div className="dbs-download-menu-item">
                  <img src={DownloadIcon} alt="download" className="dbs-download-icon" />
                  {option.label}
                </div>
              </List.Item>
            ));
          }
          // Default: Download Chart + Download Excel
          return (
            <>
              <List.Item onClick={handleDownloadChart}>
                <div className="dbs-download-menu-item">
                  <img src={DownloadIcon} alt="download" className="dbs-download-icon" />
                  {formatMessage({ id: 'dbs_download_chart' })}
                </div>
              </List.Item>
              <List.Item onClick={handleDownloadExcel}>
                <div className="dbs-download-menu-item">
                  <img src={DownloadIcon} alt="download" className="dbs-download-icon" />
                  {formatMessage({ id: 'dbs_download_excel' })}
                </div>
              </List.Item>
            </>
          );
        };

        return (
          <div className='dbs-card-slot-item' key={index as number}>
            <CommonBubble
              content={tips}
              dark
              closeOnScroll
              key={item.value}
              arrowPointAtCenter={false}
              placement={item.placement as any}
            >
              <Popover
                trigger="click"
                placement="bottom-end"
                visible={downloadMenuVisible}
                onVisibleChange={setDownloadMenuVisible}
                overlay={
                  <DropdownBox>
                    <List type="option">
                      {renderDownloadMenuItems()}
                    </List>
                  </DropdownBox>
                }
              >
                <i className={`${item.value && item.value === operation ? 'card-slot-action' : 'card-slot-item'} ${'dbo-show'}`}>
                  {item.icon && (item.isImg ? (
                    <img
                      style={{ transform: `rotate(${item.rotate || 0}deg)` }}
                      className="card-slot-charicon"
                      src={item.icon}
                    ></img>
                  ) : (
                    <SvgIcon
                      type={isArray(item.icon) ? format ? item.icon[0] : item.icon[1] : item.icon}
                      style={{ transform: `rotate(${item.rotate || 0}deg)` }}
                      className="card-slot-charicon"
                    />
                  ))}
                </i>
              </Popover>
            </CommonBubble>
          </div>
        );
      }

      // Default behavior for non-download items or when dropdown is disabled
      return <div className='dbs-card-slot-item' key={index}>
        <CommonBubble
          content={tips}
          dark
          closeOnScroll
          key={item.value}
          arrowPointAtCenter={false}
          placement={item.placement as any}
        >
          <i className={`${item.value && item.value === operation ? 'card-slot-action' : 'card-slot-item'} ${'dbo-show'}`}
            onClick={
              () => {
                if (item.value === operation) return;
                item?.value && !item?.isNoSelect && setOperation(item?.value);
                if (item?.format) {
                  setFormat(!format);
                  const data = options.find((item: any) => item.value === operation);
                  change({ ...data, format: !format });
                } else {
                  change({ ...item, format });
                }
                if (item.value === 'download' && !enableDownloadDropdown) {
                  const data = options.find((item: any) => item.value === operation);
                  change({ ...data, format }, item.value);
                  if (downloadParams?.exportExcel) {
                    downloadParams.exportExcel();
                  } else {
                    writeFile(dealDownLoadData(downloadParams), downloadParams.name);
                  }
                }
              }
            }
          >
            {item.icon && (item.isImg ? (
              <img
                style={{ transform: `rotate(${item.rotate || 0}deg)` }}
                className="card-slot-charicon"
                src={item.icon}
              ></img>
            ) : (
              <SvgIcon
                type={isArray(item.icon) ? format ? item.icon[0] : item.icon[1] : item.icon}
                style={{ transform: `rotate(${item.rotate || 0}deg)` }} // eyes-closed
                className="card-slot-charicon"
              />
            ))}
          </i>
        </CommonBubble>
      </div>;
    })}

  </>;
};
const Operations = React.forwardRef(renderOperations);
export default Operations;
